import {
  createContext,
  FC,
  PropsWithChildren,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useLayoutEffect
} from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getRebatesTableDocumentInfo } from '../Rebates/RebatesTable/api'
import { getUser } from '../../../redux/store/user/getters'
import { getUserCommunity } from '../../../helper/common'
import { useFinancialsRebatesDocGeneration } from '../../PDFGenerator/Financials/useFinancialsRebatesDocGeneration'
import { getCognusARTableDocumentInfo } from '../CognusAR/CognusARTable/api'
import { useFinancialsCognusARDocGeneration } from '../../PDFGenerator/Financials/useFinancialsCognusARDocGeneration'
import { useFinancialsAbility } from './FinancialsAbilityProvider'
import { setLoaderProps, setLoading } from '../../../redux/store/common/slice'
import useTabs, {
  TabsReturnActions,
  TabsReturnState
} from '../../../hooks/useTabs'
import { FINANCIAL_TABS } from '../constants'
import { notification } from '../../../components/Notification'
import { VALIDATION_MESSAGES } from '../../../constants'
import { ACTIONS, SUBJECTS } from '../Abilities'
import { useFinancialsAdminDocGeneration } from '../../PDFGenerator/Financials/useFinancialsAdminDocGeneration'

import { getAdminTableDocumentInfo } from '../Admin/api'
import { ROLES } from '../../Permission'
import { LedByUnion } from '../../ContractCategoryDetails/types'

type ContextProps = {
  state: TabsReturnState & {
    id: string
    hasBankAccount: boolean
    businessResource: null
    isVendor: boolean
  }
  actions: TabsReturnActions & {
    setId: Dispatch<SetStateAction<string>>
    generateAndDownloadRebateDocument: (id: string) => void
    generateAndDownloadCognusARDocument: (id: string) => void
    generateAndDownloadAdminDocument: (
      id: string,
      isInvoices: boolean,
      selectedLedBy?: LedByUnion
    ) => void
  }
}
const FinancialsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const FinancialsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [hasBankAccount, setHasBankAccount] = useState(false)
  const [businessResource, setBusinessResource] = useState<null>(null)
  const user = useSelector(getUser)
  const [id, setId] = useState('')
  const { tab } = useParams()
  const dispatch = useDispatch()
  const ability = useFinancialsAbility()
  const canSeeLedBySubTabs = ability.can(
    ACTIONS.VIEW,
    SUBJECTS.ADMIN_SPLIT_FEES
  )
  useEffect(() => {
    onGetBankAccount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    generateCommunityRebateRemitAdviceDoc,
    generateCommonsRebateRemitAdviceDoc,
    generateVendorRebateInvoiceDoc
  } = useFinancialsRebatesDocGeneration()
  const {
    generateCommonsCognusARRemitAdviceDoc,
    generateCommunityCognusARInvoiceDoc,
    generateVendorCognusARInvoiceDoc
  } = useFinancialsCognusARDocGeneration()

  const {
    generateCommunityInvoicesAdminDoc,
    generateGPOInvoicesAdminDoc,
    generateGPORemittanceAdviceAdminDoc,
    generateVendorInvoicesAdminDoc,
    generateCommunityRemitAdvicesAdminDoc,
    generateHSRemitAdvicesAdminDoc,
    generateCommonsAdminDoc
  } = useFinancialsAdminDocGeneration()

  const isVendor = !!user.vendor
  const isCommons = !!user.commons
  const isGPO = !!user.gpo
  const isCommunity = !!getUserCommunity(user)

  const availableTabs = useMemo(
    () =>
      FINANCIAL_TABS.filter((tab) => ability.can(ACTIONS.VIEW, tab.label)) ||
      [],
    [ability]
  )

  const { state: tabsState, actions: tabsActions } = useTabs({
    tabs: availableTabs,
    activeTab: tab || isGPO ? '1' : availableTabs[0]?.key
  })

  const _getTableRebatesDocumentData = useCallback(async (id: string) => {
    if (!id.length) {
      throw new Error('Wrong InvoiceID provided')
    }

    const result = await getRebatesTableDocumentInfo(id)
    if (result?.data) {
      return result.data
    }
    return undefined
  }, [])

  const _getTableCognusARDocumentData = useCallback(async (monthId: string) => {
    if (!monthId.length) {
      throw new Error('Wrong InvoiceID provided')
    }

    const result = await getCognusARTableDocumentInfo(monthId)
    if (result?.data) {
      return result.data
    }
    return undefined
  }, [])

  const _getTableAdminDocumentData = useCallback(
    async (
      monthId: string,
      isInvoices: boolean,
      selectedLedBy?: LedByUnion
    ) => {
      if (!monthId.length) {
        throw new Error('Wrong InvoiceID provided')
      }
      const result = await getAdminTableDocumentInfo(
        monthId,
        isInvoices,
        selectedLedBy
      )
      if (result?.data) {
        return result.data
      }
      return undefined
    },
    []
  )

  const _documentGenerationLoadingWrapper = useCallback(
    async (
      documentGeneration: (callback: () => void) => Promise<void>,
      successMessage: string,
      errorMessage: string,
      documentNameInfo: string
    ) => {
      dispatch(
        setLoaderProps({
          isFileUpload: true,
          message: `Generating ${documentNameInfo} document.`
        })
      )
      dispatch(setLoading(true))
      // reset loading callback
      // show success message if called from generation hook
      const callback = (isFail = false) => {
        dispatch(setLoading(false))
        dispatch(
          setLoaderProps({
            isFileUpload: false,
            message: ''
          })
        )
        if (!isFail) {
          notification.success({
            message: successMessage
          })
        }
      }

      try {
        await documentGeneration(callback)
      } catch (e) {
        notification.error({
          message: errorMessage
        })
        callback(true)
      }
    },
    [dispatch]
  )

  const generateAndDownloadRebateDocument = useCallback(
    async (id: string) => {
      const documentNameInfo = isVendor ? 'Invoice' : 'Remittance Advice'
      const successMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0167}`
      const errorMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0168}`
      await _documentGenerationLoadingWrapper(
        async (callback) => {
          const docData = await _getTableRebatesDocumentData(id)
          if (docData) {
            if (isVendor) {
              generateVendorRebateInvoiceDoc({
                vendorData: docData,
                callback
              })
            } else if (isCommons) {
              generateCommonsRebateRemitAdviceDoc({
                commonsData: docData,
                callback
              })
            } else {
              generateCommunityRebateRemitAdviceDoc({
                communityData: docData,
                callback
              })
            }
          } else {
            throw ''
          }
        },
        successMessage,
        errorMessage,
        documentNameInfo
      )
    },
    [
      _documentGenerationLoadingWrapper,
      _getTableRebatesDocumentData,
      generateCommonsRebateRemitAdviceDoc,
      generateCommunityRebateRemitAdviceDoc,
      generateVendorRebateInvoiceDoc,
      isCommons,
      isVendor
    ]
  )

  const generateAndDownloadCognusARDocument = useCallback(
    async (monthId: string) => {
      const documentNameInfo = isCommons ? 'Remittance Advice' : 'Invoice'
      const successMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0167}`
      const errorMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0168}`
      await _documentGenerationLoadingWrapper(
        async (callback) => {
          const docData = await _getTableCognusARDocumentData(monthId)
          if (docData) {
            if (isVendor) {
              generateVendorCognusARInvoiceDoc({
                vendorData: docData,
                callback
              })
            } else if (isCommons) {
              generateCommonsCognusARRemitAdviceDoc({
                commonsData: docData,
                callback
              })
            } else {
              generateCommunityCognusARInvoiceDoc({
                communityData: docData,
                callback
              })
            }
          } else {
            throw ''
          }
        },
        successMessage,
        errorMessage,
        documentNameInfo
      )
    },
    [
      _documentGenerationLoadingWrapper,
      _getTableCognusARDocumentData,
      generateCommonsCognusARRemitAdviceDoc,
      generateCommunityCognusARInvoiceDoc,
      generateVendorCognusARInvoiceDoc,
      isCommons,
      isVendor
    ]
  )

  const generateAndDownloadAdminDocument = useCallback(
    async (
      monthId: string,
      isInvoices: boolean,
      selectedLedBy?: LedByUnion
    ) => {
      const documentNameInfo = isInvoices ? 'Invoice' : 'Remittance Advice'
      const successMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0167}`
      const errorMessage = `${documentNameInfo} ${VALIDATION_MESSAGES.SM0168}`
      await _documentGenerationLoadingWrapper(
        async (callback) => {
          const docData = await _getTableAdminDocumentData(
            monthId,
            isInvoices,
            selectedLedBy
          )
          if (docData) {
            if (isInvoices) {
              if (isVendor) {
                generateVendorInvoicesAdminDoc({
                  vendorData: docData,
                  callback
                })
              }
              if (isGPO) {
                generateGPOInvoicesAdminDoc({
                  gpoData: docData,
                  callback
                })
              }
              if (isCommunity) {
                generateCommunityInvoicesAdminDoc({
                  communityData: docData,
                  callback
                })
              }
            } else {
              if (user.role === ROLES.COMMUNITY_PRESIDENT) {
                generateCommunityRemitAdvicesAdminDoc({
                  communityData: docData,
                  callback
                })
              } else if (isCommunity) {
                generateHSRemitAdvicesAdminDoc({
                  communityData: docData,
                  callback
                })
              } else if (isCommons) {
                generateCommonsAdminDoc({
                  commonsData: docData,
                  callback
                })
              } else if (isGPO) {
                generateGPORemittanceAdviceAdminDoc({
                  gpoData: docData,
                  callback
                })
              }
            }
          } else {
            throw ''
          }
        },
        successMessage,
        errorMessage,
        documentNameInfo
      )
    },
    [
      _documentGenerationLoadingWrapper,
      _getTableAdminDocumentData,
      user.role,
      isVendor,
      isGPO,
      isCommons,
      isCommunity,
      generateCommunityInvoicesAdminDoc,
      generateGPOInvoicesAdminDoc,
      generateCommunityRemitAdvicesAdminDoc,
      generateVendorInvoicesAdminDoc,
      generateHSRemitAdvicesAdminDoc,
      generateCommonsAdminDoc,
      generateGPORemittanceAdviceAdminDoc
    ]
  )

  const context = useMemo(
    () => ({
      state: {
        ...tabsState,
        hasBankAccount,
        businessResource,
        id,
        isVendor
      },
      actions: {
        ...tabsActions,
        generateAndDownloadCognusARDocument,
        generateAndDownloadRebateDocument,
        generateAndDownloadAdminDocument,
        setId
      }
    }),
    [
      tabsState,
      hasBankAccount,
      businessResource,
      id,
      tabsActions,
      generateAndDownloadCognusARDocument,
      generateAndDownloadRebateDocument,
      generateAndDownloadAdminDocument,
      isVendor
    ]
  )

  useEffect(() => {
    if (tab) tabsActions.setActiveTab(tab)
  }, [tab, tabsActions])

  useLayoutEffect(() => {
    tabsActions.setTabs(
      FINANCIAL_TABS.map((i) => {
        const isSplitDanger =
          i.key === FINANCIAL_TABS[3].key && !canSeeLedBySubTabs
        // ECOG01-12040 -- [Financials] Hide Admin fee tab for everyone
        const isAdminDanger = i.key === FINANCIAL_TABS[1].key

        return {
          ...i,
          danger: isSplitDanger || isAdminDanger
        }
      })
    )
  }, [tabsActions, canSeeLedBySubTabs])

  const onGetBankAccount = async () => {
    setBusinessResource(null)
    setHasBankAccount(false)
  }

  return (
    <FinancialsContext.Provider value={context}>
      {children}
    </FinancialsContext.Provider>
  )
}

export const useFinancialsContext = () => useContext(FinancialsContext)

export default FinancialsProvider
